import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

type ExpenseCategory =
  | 'SUPPLIER_PAYMENT'
  | 'SALARY'
  | 'RENT'
  | 'UTILITIES'
  | 'MARKETING'
  | 'TRAVEL'
  | 'OFFICE_SUPPLIES'
  | 'INSURANCE'
  | 'TAXES'
  | 'OTHER';

interface ExpenseFormData {
  category: ExpenseCategory;
  amount: number;
  currency: string;
  description: string;
  vendorName?: string;
  expenseDate: string;
  taxRate: number;
  partnerId?: string;
  bookingId?: string;
  supplierInvoiceNumber?: string;
  supplierInvoiceDate?: string;
  isReimbursable: boolean;
  notes?: string;
}

interface ExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (expenseId: string) => void;
  editId?: string;
}

const categoryKeys: Record<ExpenseCategory, string> = {
  SUPPLIER_PAYMENT: 'category.supplierPayment',
  SALARY: 'category.salary',
  RENT: 'category.rent',
  UTILITIES: 'category.utilities',
  MARKETING: 'category.marketing',
  TRAVEL: 'category.travel',
  OFFICE_SUPPLIES: 'category.officeSupplies',
  INSURANCE: 'category.insurance',
  TAXES: 'category.taxes',
  OTHER: 'category.other',
};

function getTodayString(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

export function ExpenseModal({
  open,
  onOpenChange,
  onSuccess,
  editId,
}: ExpenseModalProps) {
  const { t } = useTranslation('expenses');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ExpenseFormData>({
    defaultValues: {
      category: 'OTHER',
      currency: 'TRY',
      taxRate: 20,
      isReimbursable: false,
      expenseDate: getTodayString(),
    },
  });

  const { data: partners, isLoading: partnersLoading } = trpc.partner.list.useQuery({
    page: 1,
  });

  const { data: bookings, isLoading: bookingsLoading } = trpc.booking.list.useQuery({
    page: 1,
  });

  const { data: existingExpense, isLoading: expenseLoading } = trpc.expense.byId.useQuery(
    editId ?? '',
    { enabled: !!editId }
  );

  // Load existing expense data for editing
  useEffect(() => {
    if (existingExpense && editId) {
      setValue('category', existingExpense.category as ExpenseCategory);
      setValue('amount', existingExpense.amount);
      setValue('currency', existingExpense.currency);
      setValue('description', existingExpense.description);
      setValue('vendorName', existingExpense.vendorName ?? '');
      setValue('expenseDate', new Date(existingExpense.expenseDate).toISOString().split('T')[0] ?? '');
      setValue('taxRate', existingExpense.taxRate);
      setValue('partnerId', existingExpense.partner?.id ?? undefined);
      setValue('bookingId', existingExpense.booking?.id ?? undefined);
      setValue('supplierInvoiceNumber', existingExpense.supplierInvoiceNumber ?? '');
      setValue('supplierInvoiceDate', existingExpense.supplierInvoiceDate
        ? new Date(existingExpense.supplierInvoiceDate).toISOString().split('T')[0]
        : undefined);
      setValue('isReimbursable', existingExpense.isReimbursable);
      setValue('notes', existingExpense.notes ?? '');
    }
  }, [existingExpense, editId, setValue]);

  const utils = trpc.useUtils();

  const createMutation = trpc.expense.create.useMutation({
    onSuccess: (data) => {
      reset();
      onOpenChange(false);
      onSuccess?.(data.id);
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('createExpense') + ' - Success');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('expenseModal.createFailed'));
    },
  });

  const updateMutation = trpc.expense.update.useMutation({
    onSuccess: (data) => {
      reset();
      onOpenChange(false);
      onSuccess?.(data.id);
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      utils.expense.byId.invalidate(editId);
      showSuccessToast(t('editExpense') + ' - Success');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('expenseModal.updateFailed'));
    },
  });

  const watchedAmount = watch('amount') || 0;
  const watchedTaxRate = watch('taxRate') || 0;
  const watchedCurrency = watch('currency') || 'TRY';
  const watchedCategory = watch('category');
  const watchedPartnerId = watch('partnerId');
  const watchedBookingId = watch('bookingId');

  const taxAmount = (watchedAmount * watchedTaxRate) / 100;
  const netAmount = watchedAmount - taxAmount;

  const onSubmit = (data: ExpenseFormData) => {
    setIsSubmitting(true);

    const payload = {
      category: data.category,
      amount: Number(data.amount),
      currency: data.currency,
      description: data.description,
      vendorName: data.vendorName || undefined,
      expenseDate: new Date(data.expenseDate),
      taxRate: Number(data.taxRate),
      partnerId: data.partnerId && data.partnerId !== '_none' ? data.partnerId : undefined,
      bookingId: data.bookingId && data.bookingId !== '_none' ? data.bookingId : undefined,
      supplierInvoiceNumber: data.supplierInvoiceNumber || undefined,
      supplierInvoiceDate: data.supplierInvoiceDate ? new Date(data.supplierInvoiceDate) : undefined,
      isReimbursable: data.isReimbursable,
      notes: data.notes || undefined,
    };

    if (editId) {
      updateMutation.mutate({ id: editId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = expenseLoading && !!editId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editId ? t('editExpense') : t('createExpense')}</DialogTitle>
          <DialogDescription>
            {editId ? t('expenseModal.updateDescription') : t('expenseModal.addDescription')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Category and Date */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('form.category')} *</Label>
                <Combobox
                  options={Object.entries(categoryKeys).map(([value, labelKey]) => ({
                    value,
                    label: t(labelKey),
                  }))}
                  value={watchedCategory || 'OTHER'}
                  onValueChange={(value) => setValue('category', value as ExpenseCategory)}
                  placeholder={t('form.selectCategory')}
                  searchPlaceholder={t('expenseModal.searchCategories')}
                />
                {errors.category && (
                  <p className="text-sm text-red-500">{t('validation.categoryRequired')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="expenseDate">{t('form.expenseDate')} *</Label>
                <Input
                  id="expenseDate"
                  type="date"
                  {...register('expenseDate', { required: t('validation.dateRequired') })}
                />
                {errors.expenseDate && (
                  <p className="text-sm text-red-500">{errors.expenseDate.message}</p>
                )}
              </div>
            </div>

            {/* Amount, Currency and Tax */}
            <div className="grid gap-4 sm:grid-cols-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="amount">{t('form.amount')} *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('amount', {
                    required: t('validation.amountRequired'),
                    min: { value: 0.01, message: t('validation.amountPositive') }
                  })}
                />
                {errors.amount && (
                  <p className="text-sm text-red-500">{errors.amount.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('form.currency')}</Label>
                <Combobox
                  options={[
                    { value: 'TRY', label: 'TRY' },
                    { value: 'USD', label: 'USD' },
                    { value: 'EUR', label: 'EUR' },
                    { value: 'GBP', label: 'GBP' },
                  ]}
                  value={watchedCurrency}
                  onValueChange={(value) => setValue('currency', value)}
                  placeholder={t('form.currency')}
                  searchPlaceholder={t('expenseModal.search')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="taxRate">{t('form.taxRate')}</Label>
                <Combobox
                  options={[
                    { value: '0', label: '0%' },
                    { value: '1', label: '1%' },
                    { value: '10', label: '10%' },
                    { value: '20', label: '20%' },
                  ]}
                  value={String(watchedTaxRate)}
                  onValueChange={(value) => setValue('taxRate', Number(value))}
                  placeholder={t('form.taxRate')}
                  searchPlaceholder={t('expenseModal.search')}
                />
              </div>
            </div>

            {/* Tax and Net Amount Display */}
            {watchedAmount > 0 && (
              <div className="flex justify-end">
                <div className="w-64 space-y-2 rounded-lg bg-muted p-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('form.amount')}</span>
                    <span>{formatCurrency(watchedAmount, watchedCurrency)}</span>
                  </div>
                  {taxAmount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('form.taxAmount')} ({watchedTaxRate}%)</span>
                      <span className="text-orange-600">-{formatCurrency(taxAmount, watchedCurrency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 font-bold">
                    <span>{t('form.netAmount')}</span>
                    <span>{formatCurrency(netAmount, watchedCurrency)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Vendor and Description */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="vendorName">{t('form.vendorName')}</Label>
                <Input
                  id="vendorName"
                  {...register('vendorName')}
                  placeholder={t('form.vendorPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('form.linkToPartner')}</Label>
                <Combobox
                  options={[
                    { value: '_none', label: '-' },
                    ...(partnersLoading
                      ? []
                      : (partners?.partners.map((partner) => ({
                          value: partner.id,
                          label: partner.companyName,
                        })) ?? [])),
                  ]}
                  value={watchedPartnerId || '_none'}
                  onValueChange={(value) => setValue('partnerId', value === '_none' ? undefined : value)}
                  placeholder={t('form.selectPartner')}
                  searchPlaceholder={t('expenseModal.searchPartners')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('form.description')} *</Label>
              <Textarea
                id="description"
                {...register('description', { required: t('validation.descriptionRequired') })}
                placeholder={t('form.descriptionPlaceholder')}
                rows={2}
              />
              {errors.description && (
                <p className="text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Booking Link and Supplier Invoice */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t('form.linkToBooking')}</Label>
                <Combobox
                  options={[
                    { value: '_none', label: '-' },
                    ...(bookingsLoading
                      ? []
                      : (bookings?.bookings.map((booking) => ({
                          value: booking.id,
                          label: `${booking.bookingReference} - ${booking.customer.firstName} ${booking.customer.lastName}`,
                        })) ?? [])),
                  ]}
                  value={watchedBookingId || '_none'}
                  onValueChange={(value) => setValue('bookingId', value === '_none' ? undefined : value)}
                  placeholder={t('form.selectBooking')}
                  searchPlaceholder={t('expenseModal.searchBookings')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplierInvoiceNumber">{t('form.supplierInvoice')}</Label>
                <Input
                  id="supplierInvoiceNumber"
                  {...register('supplierInvoiceNumber')}
                  placeholder={t('form.supplierInvoicePlaceholder')}
                />
              </div>
            </div>

            {/* Supplier Invoice Date and Reimbursable */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="supplierInvoiceDate">{t('form.supplierInvoiceDate')}</Label>
                <Input
                  id="supplierInvoiceDate"
                  type="date"
                  {...register('supplierInvoiceDate')}
                />
              </div>

              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="isReimbursable"
                  {...register('isReimbursable')}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="isReimbursable" className="font-normal">
                  {t('form.isReimbursable')}
                </Label>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('form.notes')}</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder={t('form.notesPlaceholder')}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {t('expenseModal.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editId ? t('expenseModal.updateExpense') : t('expenseModal.createExpense')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
